import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Warning, ClockCountdown, CaretDown, CaretUp, Package, User, CalendarBlank,
  MagnifyingGlass, CaretLeft, CaretRight, ArrowLeft, UsersThree,
} from '@phosphor-icons/react';
import { ModuleShell, SkeletonBlock } from '../../../components/module-ui';
import { apiPath } from '../../../config/apiBase';
import { authHeaders, thStyle, tdStyle, ticketClientBranchLabel } from '../api/ticketsApi';
import TicketStatusBadge from '../components/TicketStatusBadge';
import { TicketDetailLink } from '../ticketNav';

const TICKETS_API = apiPath('/api/tickets');
const PRODUCTS_API = apiPath('/api/products');
const PAGE_SIZE = 10;

const STATE_COLORS = {
  breached: { bg: '#FEE2E2', fg: '#B91C1C', border: '#FECACA' },
  at_risk: { bg: '#FEF3C7', fg: '#B45309', border: '#FDE68A' },
  assignment_breached: { bg: '#FCE7F3', fg: '#9D174D', border: '#FBCFE8' },
  assignment_at_risk: { bg: '#E0E7FF', fg: '#3730A3', border: '#C7D2FE' },
};

function StateBadge({ state }) {
  const c = STATE_COLORS[state] || { bg: '#F3F4F6', fg: '#374151', border: '#E5E7EB' };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        background: c.bg,
        color: c.fg,
        border: `1px solid ${c.border}`,
        textTransform: 'capitalize',
        whiteSpace: 'nowrap',
      }}
    >
      {(state || '').replace(/_/g, ' ')}
    </span>
  );
}

function PipelineTrack({ pins = [] }) {
  return (
    <ol className="sla-pipeline-track" aria-label="Ticket pipeline">
      {pins.map((pin, idx) => (
        <li
          key={`${pin.pin}-${idx}`}
          className={`sla-pipeline-pin${pin.critical ? ' is-critical' : ''}${pin.done ? ' is-done' : ''}`}
        >
          <div className="sla-pipeline-pin-dot" aria-hidden />
          <div className="sla-pipeline-pin-body">
            <div className="sla-pipeline-pin-label">{pin.label}</div>
            <div className="sla-pipeline-pin-what"><strong>What:</strong> {pin.what || '—'}</div>
            <div className="sla-pipeline-pin-who"><strong>Who:</strong> {pin.who || '—'}</div>
            <div className="sla-pipeline-pin-when"><strong>When:</strong> {pin.when_ist || '—'}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}

function roleLabel(role) {
  const r = String(role || '').toLowerCase();
  if (r === 'manager') return 'Manager';
  if (r === 'lead') return 'Lead';
  if (r === 'member') return 'Member';
  return role ? String(role) : '—';
}

/**
 * Product 360° — workload, team roster, clients, and open-ticket pipeline for one product.
 * Replaces the thin pipeline-only page when product_id is present.
 */
export default function Product360Page({ productId }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assignedTo = searchParams.get('assigned_to') || '';
  const client = searchParams.get('client') || '';

  const [loadingMeta, setLoadingMeta] = useState(true);
  const [teamProduct, setTeamProduct] = useState(null);
  const [clients, setClients] = useState([]);
  const [metaError, setMetaError] = useState(null);

  const [loadingTickets, setLoadingTickets] = useState(true);
  const [payload, setPayload] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState({});
  const [ticketError, setTicketError] = useState(null);
  /** Which KPI card drives the ticket list: active | open | breached | at_risk | closed */
  const [viewMode, setViewMode] = useState('active');
  /** Stable chip counts (not overwritten when filtering the list). */
  const [kpiOpen, setKpiOpen] = useState({ matched: 0, breached: 0, at_risk: 0 });

  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDetail, setMemberDetail] = useState(null);
  const [memberLoading, setMemberLoading] = useState(false);
  const rosterRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const loadMeta = async () => {
      setLoadingMeta(true);
      setMetaError(null);
      try {
        const [teamsRes, clientsRes, managerRes] = await Promise.all([
          axios.get(`${TICKETS_API}/dashboard/product-teams`, {
            ...authHeaders(),
            params: { limit_products: 50 },
          }).catch(() => ({ data: { items: [] } })),
          axios.get(`${TICKETS_API}/dashboard/by-client`, {
            ...authHeaders(),
            params: { product_id: productId, open_only: true, limit: 12 },
          }).catch(() => ({ data: { items: [] } })),
          axios.get(`${PRODUCTS_API}/manager/teams`, authHeaders()).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const items = teamsRes.data?.items || [];
        let found = items.find((p) => p.product_id === productId) || null;
        if (!found) {
          const mgrTeam = (managerRes.data || []).find((p) => p.product_id === productId);
          if (mgrTeam) {
            found = {
              product_id: mgrTeam.product_id,
              product_name: mgrTeam.product_name,
              product_key: mgrTeam.product_key,
              member_count: mgrTeam.member_count,
              active_total: mgrTeam.active_total,
              closed_total: mgrTeam.closed_total,
              manager: mgrTeam.manager,
              members: (mgrTeam.members || []).map((m) => ({
                ...m,
                doing: [
                  m.open_count ? `${m.open_count} open` : null,
                  m.in_progress_count ? `${m.in_progress_count} in progress` : null,
                ].filter(Boolean).join(', ') || 'Idle',
              })),
            };
          }
        }
        setTeamProduct(found);
        setClients(clientsRes.data?.items || []);
        setViewMode('active');
      } catch (e) {
        if (!cancelled) {
          setMetaError(e.response?.data?.detail || 'Could not load product overview');
          setTeamProduct(null);
          setClients([]);
        }
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    };
    loadMeta();
    return () => { cancelled = true; };
  }, [productId]);

  // Baseline open / SLA counts for the KPI chips (independent of selected card).
  useEffect(() => {
    let cancelled = false;
    axios.get(`${TICKETS_API}/dashboard/ticket-pipeline`, {
      ...authHeaders(),
      params: {
        product_id: productId,
        status_group: 'open',
        page: 1,
        page_size: 1,
        ...(assignedTo ? { assigned_to: assignedTo } : {}),
        ...(client ? { client } : {}),
      },
    }).then((res) => {
      if (cancelled) return;
      const s = res.data?.summary || {};
      setKpiOpen({
        matched: s.matched || 0,
        breached: s.breached || 0,
        at_risk: s.at_risk || 0,
      });
    }).catch(() => {
      if (!cancelled) setKpiOpen({ matched: 0, breached: 0, at_risk: 0 });
    });
    return () => { cancelled = true; };
  }, [productId, assignedTo, client]);

  const fetchPipeline = useCallback(async () => {
    setLoadingTickets(true);
    setTicketError(null);
    try {
      const params = {
        page,
        page_size: PAGE_SIZE,
        product_id: productId,
        status_group: viewMode === 'closed' ? 'closed' : 'open',
      };
      if (viewMode === 'breached') params.pipeline_state = 'breached';
      if (viewMode === 'at_risk') params.pipeline_state = 'at_risk';
      if (assignedTo) params.assigned_to = assignedTo;
      if (client) params.client = client;
      if (q.trim()) params.q = q.trim();
      const res = await axios.get(`${TICKETS_API}/dashboard/ticket-pipeline`, {
        ...authHeaders(),
        params,
      });
      setPayload(res.data);
    } catch (e) {
      setTicketError('Could not load tickets');
      setPayload(null);
    } finally {
      setLoadingTickets(false);
    }
  }, [productId, assignedTo, client, page, q, viewMode]);

  useEffect(() => { fetchPipeline(); }, [fetchPipeline]);

  useEffect(() => {
    setPage(1);
    setExpanded({});
  }, [productId, assignedTo, client, viewMode]);

  const selectView = (mode) => {
    if (mode === 'members') {
      rosterRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    setViewMode(mode);
  };

  useEffect(() => {
    const t = setTimeout(() => {
      const next = searchInput.trim();
      if (next === q) return;
      setPage(1);
      setQ(next);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput, q]);

  const openMember = async (member) => {
    setSelectedMember(member);
    setMemberLoading(true);
    setMemberDetail(null);
    try {
      const res = await axios.get(
        `${PRODUCTS_API}/manager/teams/${productId}/members/${member.user_id}`,
        authHeaders(),
      );
      setMemberDetail(res.data);
    } catch {
      // Manager endpoint may 403 for non-managers — fall back to pipeline filter.
      setMemberDetail(null);
      navigate(`/dashboard/tickets/pipeline?product_id=${encodeURIComponent(productId)}&assigned_to=${encodeURIComponent(member.user_id)}`);
      setSelectedMember(null);
    } finally {
      setMemberLoading(false);
    }
  };

  const members = teamProduct?.members || [];
  const maxActive = useMemo(
    () => Math.max(1, ...members.map((m) => Number(m.active_count) || 0)),
    [members],
  );

  const productName = teamProduct?.product_name
    || payload?.title
    || productId;
  const items = payload?.items || [];
  const pagination = payload?.pagination || {
    page: 1, page_size: PAGE_SIZE, total: 0, total_pages: 1, has_prev: false, has_next: false,
  };
  const from = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.page_size + 1;
  const to = Math.min(pagination.page * pagination.page_size, pagination.total);

  const VIEW_META = {
    active: {
      title: 'Active ticket pipeline',
      hint: 'All non-closed tickets for this product — expand a row for stage pins',
    },
    open: {
      title: 'Open ticket pipeline',
      hint: 'Open tickets currently in the product pipeline',
    },
    breached: {
      title: 'SLA breached tickets',
      hint: 'Open tickets past their resolution SLA',
    },
    at_risk: {
      title: 'SLA at-risk tickets',
      hint: 'Open tickets nearing their resolution SLA',
    },
    closed: {
      title: 'Closed tickets',
      hint: 'Closed / completed tickets for this product',
    },
  };
  const viewMeta = VIEW_META[viewMode] || VIEW_META.active;

  const listLink = () => {
    const sp = new URLSearchParams({
      status_group: viewMode === 'closed' ? 'closed' : 'open',
      product_id: productId,
    });
    if (assignedTo) sp.set('assigned_to', assignedTo);
    return `/dashboard/tickets/list?${sp.toString()}`;
  };

  if (loadingMeta && !teamProduct) {
    return (
      <ModuleShell title="Product 360°" subtitle="Loading…">
        <SkeletonBlock rows={10} />
      </ModuleShell>
    );
  }

  return (
    <ModuleShell
      title={productName}
      subtitle="Product 360° — team, workload, clients, and open ticket pipeline"
      headerActions={(
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="tk-btn tk-btn-secondary" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={16} />
            Overview
          </button>
          <Link to="/dashboard/my-teams" className="tk-btn tk-btn-secondary">
            My Teams
          </Link>
          <Link to={listLink()} className="tk-btn tk-btn-secondary">
            Open ticket list
          </Link>
        </div>
      )}
    >
      <div data-testid="product-360-page" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {metaError && (
          <div style={{ padding: 12, background: '#FEE2E2', color: '#B91C1C', borderRadius: 8 }}>{metaError}</div>
        )}

        {/* KPI strip — click a card to drive the ticket list below */}
        <div className="sla-pipeline-summary" role="group" aria-label="Product summary">
          {[
            {
              id: 'members',
              value: teamProduct?.member_count ?? members.length,
              label: 'Team members',
              select: false,
            },
            {
              id: 'active',
              value: teamProduct?.active_total ?? kpiOpen.matched,
              label: 'Active tickets',
              select: true,
            },
            {
              id: 'open',
              value: kpiOpen.matched,
              label: 'Open in pipeline',
              select: true,
            },
            {
              id: 'breached',
              value: kpiOpen.breached,
              label: 'SLA breached',
              select: true,
            },
            {
              id: 'at_risk',
              value: kpiOpen.at_risk,
              label: 'SLA at risk',
              select: true,
            },
            {
              id: 'closed',
              value: teamProduct?.closed_total ?? 0,
              label: 'Closed',
              select: true,
            },
          ].map((chip) => {
            const selected = chip.select && viewMode === chip.id;
            return (
              <button
                key={chip.id}
                type="button"
                className={`sla-summary-chip${selected ? ' is-selected' : ''}`}
                onClick={() => selectView(chip.id)}
                aria-pressed={chip.select ? selected : undefined}
                style={{
                  cursor: 'pointer',
                  border: selected ? '2px solid #002FA7' : undefined,
                  background: selected ? '#EFF6FF' : undefined,
                  textAlign: 'left',
                }}
              >
                <span className="sla-summary-chip-value">{chip.value}</span>
                <span className="sla-summary-chip-label">{chip.label}</span>
              </button>
            );
          })}
        </div>
        <p style={{ margin: '-8px 0 0', fontSize: 12, color: '#6B7280' }}>
          Click a card above to change the ticket list below
          {viewMode !== 'active' ? ` · showing: ${viewMeta.title}` : ''}
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)',
          gap: 16,
          alignItems: 'start',
        }}
        className="product-360-grid"
        >
          {/* Team workload — enlarged ProductTeamsWidget style */}
          <section className="wd-chart-card wd-soft-card is-auto-height" style={{ margin: 0 }}>
            <p className="wd-chart-title">Team workload</p>
            <p className="wd-chart-subtitle">
              {teamProduct?.manager?.name
                ? `Manager: ${teamProduct.manager.name}`
                : 'No manager assigned'}
              {teamProduct?.product_key ? ` · ${teamProduct.product_key}` : ''}
            </p>
            <div className="wd-chart-body" style={{ maxHeight: 'none' }}>
              <div className="wd-product-teams">
                {members.length === 0 ? (
                  <p style={{ color: '#9CA3AF', fontSize: 13, margin: 0 }}>No staff members on this product team.</p>
                ) : (
                  <ul className="wd-product-team-members" style={{ margin: 0 }}>
                    {members.map((m) => {
                      const active = Number(m.active_count) || 0;
                      const pct = Math.round((active / maxActive) * 100);
                      const highlighted = assignedTo && assignedTo === m.user_id;
                      return (
                        <li key={m.user_id} style={{ marginBottom: 8 }}>
                          <button
                            type="button"
                            className="wd-product-team-member"
                            onClick={() => openMember(m)}
                            style={{
                              width: '100%',
                              border: highlighted ? '1px solid #002FA7' : undefined,
                              background: highlighted ? '#EFF6FF' : undefined,
                            }}
                          >
                            <div className="wd-ptm-top">
                              <span className="wd-ptm-name-wrap">
                                <span className="wd-ptm-name">{m.name}</span>
                                <span className="wd-ptm-role">{roleLabel(m.org_role)}</span>
                              </span>
                              <span className="wd-ptm-closed">{m.closed_count ?? 0} closed</span>
                            </div>
                            <div className="wd-ptm-doing">{m.doing || 'Idle'}</div>
                            <div className="wd-ptm-counts">
                              <span>{m.open_count ?? 0} open</span>
                              <span>{m.in_progress_count ?? 0} in progress</span>
                              <span>{active} active</span>
                            </div>
                            <div className="wd-product-team-track" aria-hidden style={{ marginTop: 8 }}>
                              <div className="wd-product-team-fill" style={{ width: `${pct}%` }} />
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </section>

          {/* Team roster — same as My Teams, no inner scroll */}
          <section
            ref={rosterRef}
            style={{
              border: '1px solid #E5E7EB',
              borderRadius: 12,
              background: '#fff',
              overflow: 'hidden',
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '14px 16px',
              background: '#F8FAFC',
              borderBottom: '1px solid #E5E7EB',
            }}
            >
              <UsersThree size={18} color="#002FA7" weight="fill" />
              <div>
                <div style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  color: '#002FA7',
                  textTransform: 'uppercase',
                }}
                >
                  Team roster
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{productName}</div>
              </div>
            </div>
            <div style={{ padding: '8px 12px 12px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ textAlign: 'left', background: '#FAFAFA' }}>
                    <th style={thStyle}>Member</th>
                    <th style={thStyle}>Role</th>
                    <th style={thStyle}>Open</th>
                    <th style={thStyle}>In prog</th>
                    <th style={thStyle}>Active</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.user_id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                      <td style={tdStyle}>
                        <button
                          type="button"
                          onClick={() => openMember(m)}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            color: '#002FA7',
                            fontWeight: 600,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                          }}
                        >
                          {m.name}
                        </button>
                        {m.designation && (
                          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{m.designation}</div>
                        )}
                      </td>
                      <td style={tdStyle}>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 999,
                          background: '#EFF6FF',
                          color: '#1D4ED8',
                          fontSize: 11,
                          fontWeight: 600,
                        }}
                        >
                          {roleLabel(m.org_role)}
                        </span>
                      </td>
                      <td style={tdStyle}>{m.open_count ?? 0}</td>
                      <td style={tdStyle}>{m.in_progress_count ?? 0}</td>
                      <td style={tdStyle}>{m.active_count ?? 0}</td>
                    </tr>
                  ))}
                  {members.length === 0 && (
                    <tr>
                      <td colSpan={5} style={{ ...tdStyle, color: '#9CA3AF' }}>No members on this team yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {/* Clients for this product */}
        <section className="wd-chart-card wd-soft-card is-auto-height" style={{ margin: 0 }}>
          <p className="wd-chart-title">Clients with open tickets</p>
          <p className="wd-chart-subtitle">Open volume by client for this product</p>
          {clients.length === 0 ? (
            <p style={{ color: '#9CA3AF', fontSize: 13, margin: 0 }}>No open client tickets for this product.</p>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {clients.map((c) => (
                <Link
                  key={c.key}
                  to={`/dashboard/tickets/pipeline?product_id=${encodeURIComponent(productId)}&client=${encodeURIComponent(c.key)}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    borderRadius: 10,
                    border: '1px solid #E5E7EB',
                    background: '#fff',
                    textDecoration: 'none',
                    color: '#111827',
                    fontSize: 12,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{c.name || c.key}</span>
                  <span style={{ color: '#002FA7', fontWeight: 700 }}>{c.count}</span>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Ticket list — driven by the selected KPI card above */}
        <section className="sla-pipeline-panel" style={{ margin: 0 }} data-testid="product-360-ticket-list">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{viewMeta.title}</h3>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6B7280' }}>
                {viewMeta.hint}
                {assignedTo ? ' · filtered to selected member' : ''}
                {client ? ` · client: ${client}` : ''}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {viewMode !== 'active' && (
                <button
                  type="button"
                  className="tk-btn tk-btn-secondary"
                  onClick={() => setViewMode('active')}
                >
                  Show active
                </button>
              )}
              {(assignedTo || client) && (
                <button
                  type="button"
                  className="tk-btn tk-btn-secondary"
                  onClick={() => navigate(`/dashboard/tickets/pipeline?product_id=${encodeURIComponent(productId)}`)}
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>

          <div className="sla-pipeline-filters">
            <form
              className="sla-api-search"
              onSubmit={(e) => {
                e.preventDefault();
                const next = searchInput.trim();
                setPage(1);
                setQ(next);
              }}
              role="search"
            >
              <MagnifyingGlass size={16} className="sla-api-search-icon" aria-hidden />
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search ticket, person…"
                data-testid="ticket-pipeline-search"
              />
              <button type="submit" className="tk-btn tk-btn-secondary">Search</button>
            </form>
          </div>

          {ticketError && <p style={{ color: '#B91C1C', fontSize: 13 }}>{ticketError}</p>}
          {loadingTickets && !payload ? (
            <SkeletonBlock rows={4} />
          ) : items.length === 0 ? (
            <div className="sla-pipeline-empty">
              <ClockCountdown size={28} color="#9CA3AF" />
              <p>
                {q
                  ? `No tickets match “${q}”.`
                  : `No tickets for “${viewMeta.title.replace(/ tickets$/i, '').toLowerCase()}”.`}
              </p>
            </div>
          ) : (
            <>
              <div className="sla-pipeline-grid">
                {items.map((item) => {
                  const open = !!expanded[item.ticket_no];
                  const assignees = (item.who?.assignee_names || []).join(', ') || 'Unassigned';
                  return (
                    <div
                      key={item.ticket_no}
                      className={`sla-pipeline-card${open ? ' is-expanded' : ''}`}
                      data-testid="ticket-pipeline-row"
                    >
                      <div className="sla-pipeline-row-main">
                        <div className="sla-pipeline-row-top">
                          <Link
                            to={`/dashboard/tickets/list/${encodeURIComponent(item.ticket_no)}`}
                            className="sla-ticket-link"
                          >
                            {item.ticket_no}
                          </Link>
                          <StateBadge state={item.primary_state} />
                          {item.what?.priority && (
                            <span className={`badge badge-${item.what.priority}`}>{item.what.priority}</span>
                          )}
                        </div>
                        <div className="sla-pipeline-what" title={item.what?.subject}>
                          <strong>What:</strong> {item.what?.subject || '—'}
                        </div>
                        <div className="sla-pipeline-meta">
                          <span><User size={14} /> <strong>Who:</strong> {assignees}</span>
                          <span><CalendarBlank size={14} /> <strong>When:</strong> {item.when?.due_date_ist || item.when?.created_at_ist || '—'}</span>
                          <span><Package size={14} /> <strong>App:</strong> {productName}</span>
                          <span><Warning size={14} /> <strong>Client:</strong> {item.what?.client || '—'}</span>
                        </div>
                      </div>
                      <div className="sla-pipeline-row-actions">
                        <button
                          type="button"
                          className="tk-btn tk-btn-secondary"
                          onClick={() => setExpanded((prev) => ({ ...prev, [item.ticket_no]: !prev[item.ticket_no] }))}
                          aria-expanded={open}
                        >
                          {open ? <CaretUp size={16} /> : <CaretDown size={16} />}
                          Stages
                        </button>
                      </div>
                      {open && (
                        <div className="sla-pipeline-detail">
                          <div className="sla-pipeline-detail-grid">
                            <div>
                              <h4>Reporter</h4>
                              <p>{item.who?.reporter_name || '—'}{item.who?.reporter_email ? ` · ${item.who.reporter_email}` : ''}</p>
                            </div>
                            <div>
                              <h4>Domain</h4>
                              <p>{item.what?.domain || '—'} · {item.what?.status || '—'}</p>
                            </div>
                            <div>
                              <h4>Created</h4>
                              <p>{item.when?.created_at_ist || '—'} · {item.who?.created_by || '—'}</p>
                            </div>
                            <div>
                              <h4>SLA</h4>
                              <p>
                                {item.sla_policy?.name || '—'}
                                {item.when?.due_date_ist ? ` · due ${item.when.due_date_ist}` : ''}
                              </p>
                            </div>
                          </div>
                          <PipelineTrack pins={item.pipeline || []} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="sla-pagination" data-testid="ticket-pipeline-pagination">
                <span className="sla-pagination-meta">
                  Showing {from}–{to} of {pagination.total}
                  {q ? ` for “${q}”` : ''}
                </span>
                <div className="sla-pagination-buttons">
                  <button
                    type="button"
                    className="tk-btn tk-btn-secondary"
                    disabled={!pagination.has_prev || loadingTickets}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <CaretLeft size={16} />
                    Previous
                  </button>
                  <span className="sla-pagination-page">
                    Page {pagination.page} / {pagination.total_pages}
                  </span>
                  <button
                    type="button"
                    className="tk-btn tk-btn-secondary"
                    disabled={!pagination.has_next || loadingTickets}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                    <CaretRight size={16} />
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      {selectedMember && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(17, 24, 39, 0.45)',
            zIndex: 80,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={() => { setSelectedMember(null); setMemberDetail(null); }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 720,
              maxHeight: 'min(560px, calc(100vh - 48px))',
              display: 'flex',
              flexDirection: 'column',
              background: '#fff',
              borderRadius: 12,
              border: '1px solid #E5E7EB',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '14px 16px',
              borderBottom: '1px solid #E5E7EB',
              background: '#F8FAFC',
            }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{selectedMember.name} — workload</h3>
                {memberDetail && (
                  <p style={{ fontSize: 12, color: '#6B7280', margin: '4px 0 0' }}>
                    Role: {memberDetail.member?.org_role}
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className="tk-btn tk-btn-secondary"
                  onClick={() => {
                    navigate(`/dashboard/tickets/pipeline?product_id=${encodeURIComponent(productId)}&assigned_to=${encodeURIComponent(selectedMember.user_id)}`);
                    setSelectedMember(null);
                  }}
                >
                  Filter pipeline
                </button>
                <button
                  type="button"
                  className="tk-btn tk-btn-secondary"
                  onClick={() => { setSelectedMember(null); setMemberDetail(null); }}
                >
                  Close
                </button>
              </div>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
              {memberLoading && <p style={{ color: '#6B7280' }}>Loading…</p>}
              {memberDetail && (
                memberDetail.tickets?.length === 0 ? (
                  <p style={{ color: '#6B7280' }}>No tickets assigned.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', background: '#F9FAFB' }}>
                        <th style={thStyle}>Ticket</th>
                        <th style={thStyle}>Subject</th>
                        <th style={thStyle}>Client / Branch</th>
                        <th style={thStyle}>Status</th>
                        <th style={thStyle}>Priority</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(memberDetail.tickets || []).map((t) => (
                        <tr key={t.ticket_no} style={{ borderBottom: '1px solid #E5E7EB' }}>
                          <td style={tdStyle}>
                            <TicketDetailLink ticketNo={t.ticket_no} style={{ color: '#002FA7', fontWeight: 600 }}>
                              {t.ticket_no}
                            </TicketDetailLink>
                          </td>
                          <td style={tdStyle}>{t.subject}</td>
                          <td style={tdStyle}>{ticketClientBranchLabel(t)}</td>
                          <td style={tdStyle}><TicketStatusBadge status={t.status} /></td>
                          <td style={tdStyle}>{t.priority}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 900px) {
          .product-360-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </ModuleShell>
  );
}
